import React, { useState, useEffect } from 'react';
import type { Node, Edge } from 'reactflow';
import type { FlowNodeData, EdgeCondition } from './types';
import { X, Trash2 } from 'lucide-react';

interface PropertiesPanelProps {
  selectedNode: Node<FlowNodeData> | null;
  selectedEdge: Edge | null;
  onUpdateNode: (nodeId: string, data: Partial<FlowNodeData>) => void;
  onUpdateEdge?: (edgeId: string, label: string | null, condition: EdgeCondition | null, priority: number) => void;
  onDeleteNode?: (nodeId: string) => void;
  onDeleteEdge?: (edgeId: string) => void;
  onClose: () => void;
  departments: any[];
  templates: any[];
  readOnly?: boolean;
}

export const PropertiesPanel: React.FC<PropertiesPanelProps> = ({
  selectedNode,
  selectedEdge,
  onUpdateNode,
  onUpdateEdge,
  onDeleteNode,
  onDeleteEdge,
  onClose,
  departments,
  templates,
  readOnly = false
}) => {
  const [label, setLabel] = useState('');
  const [config, setConfig] = useState<any>({});

  // Edge states
  const [edgeLabel, setEdgeLabel] = useState('');
  const [edgeConditionField, setEdgeConditionField] = useState('');
  const [edgeConditionOperator, setEdgeConditionOperator] = useState<EdgeCondition['operator']>('equals');
  const [edgeConditionValue, setEdgeConditionValue] = useState('');
  const [edgePriority, setEdgePriority] = useState(0);

  useEffect(() => {
    if (selectedNode) {
      setLabel(selectedNode.data.label || '');
      setConfig(selectedNode.data.config || {});
    }
  }, [selectedNode]);

  useEffect(() => {
    if (selectedEdge) {
      setEdgeLabel(selectedEdge.label as string || '');
      setEdgePriority(selectedEdge.data?.priority || 0);
      if (selectedEdge.data?.condition) {
        const conditionValue = selectedEdge.data.condition.value || '';
        console.log('[PropertiesPanel] Loading edge condition from selectedEdge:', selectedEdge.data.condition);
        console.log('[PropertiesPanel] Condition value:', conditionValue);
        console.log('[PropertiesPanel] Value length:', conditionValue.length);
        console.log('[PropertiesPanel] Value chars:', conditionValue.split('').map((c: string) => `${c}(${c.charCodeAt(0)})`).join(', '));

        setEdgeConditionField(selectedEdge.data.condition.field || '');
        setEdgeConditionOperator(selectedEdge.data.condition.operator || 'equals');
        setEdgeConditionValue(conditionValue);
      } else {
        setEdgeConditionField('');
        setEdgeConditionOperator('equals');
        setEdgeConditionValue('');
      }
    }
  }, [selectedEdge]);

  if (!selectedNode && !selectedEdge) {
    return (
      <div className="w-64 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-4 flex items-center justify-center">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          Selecione um nó ou conexão para configurar
        </p>
      </div>
    );
  }

  const handleConfigChange = (key: string, value: any) => {
    if (readOnly || !selectedNode) return;
    const newConfig = { ...config, [key]: value };
    setConfig(newConfig);
    // Auto-save on change
    onUpdateNode(selectedNode.id, {
      label: label || selectedNode.data.label,
      config: newConfig
    });
  };

  const handleLabelChange = (newLabel: string) => {
    if (readOnly || !selectedNode) return;
    setLabel(newLabel);
    onUpdateNode(selectedNode.id, {
      label: newLabel,
      config
    });
  };

  const renderConfigForm = () => {
    if (!selectedNode) return null;
    const nodeType = selectedNode.data.nodeType;

    switch (nodeType) {
      case 'SEND_MESSAGE':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mensagem
              </label>
              <textarea
                value={config.message || ''}
                onChange={(e) => handleConfigChange('message', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                rows={5}
                placeholder="Digite a mensagem..."
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Use variáveis: {'{{'}customer_name{'}}'}, {'{{'}dias_cancelamento{'}}'}, etc.
              </p>
            </div>
          </div>
        );

      case 'SEND_TEMPLATE':
        const templateVariables = config.templateVariables || [];

        const addVariable = () => {
          const newVars = [...templateVariables, { key: '', value: '' }];
          handleConfigChange('templateVariables', newVars);
        };

        const updateVariable = (index: number, field: 'key' | 'value', newValue: string) => {
          const newVars = [...templateVariables];
          newVars[index] = { ...newVars[index], [field]: newValue };
          handleConfigChange('templateVariables', newVars);
        };

        const removeVariable = (index: number) => {
          const newVars = templateVariables.filter((_: any, i: number) => i !== index);
          handleConfigChange('templateVariables', newVars);
        };

        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Template
              </label>
              <select
                value={config.templateName || ''}
                onChange={(e) => handleConfigChange('templateName', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">Selecione um template</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Variáveis do Template
                </label>
                {!readOnly && (
                  <button
                    onClick={addVariable}
                    className="text-xs px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                  >
                    + Adicionar
                  </button>
                )}
              </div>

              {templateVariables.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  Nenhuma variável adicionada. Clique em "+ Adicionar" para começar.
                </p>
              ) : (
                <div className="space-y-3">
                  {templateVariables.map((variable: any, index: number) => (
                    <div key={index} className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <div className="flex items-start justify-between mb-2">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Variável #{index + 1}
                        </span>
                        {!readOnly && (
                          <button
                            onClick={() => removeVariable(index)}
                            className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                            title="Remover variável"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Posição no Template
                          </label>
                          <input
                            type="text"
                            value={variable.key || ''}
                            onChange={(e) => updateVariable(index, 'key', e.target.value)}
                            disabled={readOnly}
                            placeholder="1"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60"
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Ex: 1 para {'{{1}}'}, 2 para {'{{2}}'}
                          </p>
                        </div>

                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                            Valor da Variável
                          </label>
                          <select
                            value={variable.value || ''}
                            onChange={(e) => updateVariable(index, 'value', e.target.value)}
                            disabled={readOnly}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60"
                          >
                            <option value="">Selecione uma variável</option>
                            <option value="{{contact.name}}">Nome do Cliente</option>
                            <option value="{{contact.phone}}">Telefone do Cliente</option>
                            <option value="{{protocol_id}}">Número do Protocolo</option>
                            <option value="{{current_date}}">Data Atual</option>
                            <option value="{{current_time}}">Hora Atual</option>
                          </select>
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Escolha qual informação será inserida no template
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'TRANSFER_DEPARTMENT':
        console.log('[PropertiesPanel] TRANSFER_DEPARTMENT - departments:', departments, 'config:', config);
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Departamento
              </label>
              <select
                value={config.departmentId || ''}
                onChange={(e) => {
                  if (readOnly || !selectedNode) return;

                  const deptId = e.target.value;
                  const dept = departments.find(d => d.id === deptId);

                  console.log('[PropertiesPanel] Department selected:', deptId);
                  console.log('[PropertiesPanel] Found department:', dept);

                  // Update config with both departmentId and departmentName at once
                  const newConfig = {
                    ...config,
                    departmentId: deptId,
                    departmentName: dept ? dept.name : '',
                  };

                  console.log('[PropertiesPanel] New config:', newConfig);

                  setConfig(newConfig);
                  onUpdateNode(selectedNode.id, {
                    label: label || selectedNode.data.label,
                    config: newConfig
                  });
                }}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="">Selecione um departamento</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="assignToAgent"
                checked={config.assignToAgent || false}
                onChange={(e) => handleConfigChange('assignToAgent', e.target.checked)}
                disabled={readOnly}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
              />
              <label htmlFor="assignToAgent" className="text-sm text-gray-700 dark:text-gray-300">
                Atribuir automaticamente a um agente
              </label>
            </div>
          </div>
        );

      case 'UPDATE_STATUS':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Novo Status
            </label>
            <select
              value={config.status || ''}
              onChange={(e) => handleConfigChange('status', e.target.value)}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="">Selecione um status</option>
              <option value="WAITING">Aguardando</option>
              <option value="MENU">Menu</option>
              <option value="QUEUED">Na Fila</option>
              <option value="ACTIVE">Ativo</option>
              <option value="ANSWERED">Respondida</option>
              <option value="CLOSED">Encerrada</option>
            </select>
          </div>
        );

      case 'CLOSE_CONVERSATION':
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Motivo (opcional)
            </label>
            <input
              type="text"
              value={config.reason || ''}
              onChange={(e) => handleConfigChange('reason', e.target.value)}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder="Motivo do encerramento"
            />
          </div>
        );

      case 'SET_VARIABLE':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Nome da Variável
              </label>
              <input
                type="text"
                value={config.variableName || ''}
                onChange={(e) => handleConfigChange('variableName', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="nome_variavel"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Valor
              </label>
              <input
                type="text"
                value={config.value || ''}
                onChange={(e) => handleConfigChange('value', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="Valor ou {{outra_variavel}}"
              />
            </div>
          </div>
        );

      case 'WAIT_RESPONSE':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tipo de Resposta
              </label>
              <select
                value={config.responseType || 'ANY'}
                onChange={(e) => handleConfigChange('responseType', e.target.value)}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <option value="ANY">Qualquer resposta</option>
                <option value="BUTTON_RESPONSE">Resposta de botão</option>
                <option value="TEXT">Resposta de texto</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Timeout (segundos)
              </label>
              <input
                type="number"
                value={config.timeout || ''}
                onChange={(e) => handleConfigChange('timeout', Number(e.target.value))}
                disabled={readOnly}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                placeholder="Deixe vazio para sem timeout"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Tempo máximo para aguardar a resposta (opcional)
              </p>
            </div>
          </div>
        );

      case 'START':
      case 'END':
        return (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Este nó não requer configuração adicional.
          </p>
        );

      case 'CONDITION':
        return (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            As condições são configuradas nas conexões (arestas) que saem deste nó.
            Conecte este nó a outros e configure as condições em cada conexão.
          </p>
        );

      default:
        return null;
    }
  };

  const handleEdgeLabelChange = (newLabel: string) => {
    if (readOnly || !selectedEdge || !onUpdateEdge) return;
    setEdgeLabel(newLabel);
    const condition = edgeConditionField ? {
      field: edgeConditionField,
      operator: edgeConditionOperator,
      value: edgeConditionValue
    } : null;
    onUpdateEdge(selectedEdge.id, newLabel || null, condition, edgePriority);
  };

  const handleEdgeConditionChange = (overrides?: { field?: string; operator?: EdgeCondition['operator']; value?: string }) => {
    if (readOnly || !selectedEdge || !onUpdateEdge) return;

    const finalField = overrides?.field !== undefined ? overrides.field : edgeConditionField;
    const finalOperator = overrides?.operator !== undefined ? overrides.operator : edgeConditionOperator;
    const finalValue = overrides?.value !== undefined ? overrides.value : edgeConditionValue;

    const condition = finalField ? {
      field: finalField,
      operator: finalOperator,
      value: finalValue
    } : null;

    if (condition) {
      console.log('[PropertiesPanel] Saving edge condition:', condition);
      console.log('[PropertiesPanel] Value:', condition.value);
      console.log('[PropertiesPanel] Value length:', condition.value.length);
      console.log('[PropertiesPanel] Value chars:', condition.value.split('').map((c: string) => `${c}(${c.charCodeAt(0)})`).join(', '));
    }

    onUpdateEdge(selectedEdge.id, edgeLabel || null, condition, edgePriority);
  };

  const handleEdgePriorityChange = (newPriority: number) => {
    if (readOnly || !selectedEdge || !onUpdateEdge) return;
    setEdgePriority(newPriority);
    const condition = edgeConditionField ? {
      field: edgeConditionField,
      operator: edgeConditionOperator,
      value: edgeConditionValue
    } : null;
    onUpdateEdge(selectedEdge.id, edgeLabel || null, condition, newPriority);
  };

  // Render Edge Properties
  if (selectedEdge) {
    return (
      <div className="w-64 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Conexão {readOnly && <span className="text-xs font-normal text-yellow-600 dark:text-yellow-400">(Leitura)</span>}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {!readOnly && onDeleteEdge && (
            <button
              onClick={() => {
                onDeleteEdge(selectedEdge.id);
                onClose();
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Deletar Conexão
            </button>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Rótulo (opcional)
            </label>
            <input
              type="text"
              value={edgeLabel}
              onChange={(e) => handleEdgeLabelChange(e.target.value)}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              placeholder="Ex: Se SIM, Se não..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Prioridade
            </label>
            <input
              type="number"
              value={edgePriority}
              onChange={(e) => handleEdgePriorityChange(Number(e.target.value))}
              disabled={readOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed text-sm"
              placeholder="0"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Maior prioridade é executada primeiro
            </p>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Condição (opcional)</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Campo
                </label>
                <select
                  value={edgeConditionField}
                  onChange={(e) => {
                    const newField = e.target.value;
                    setEdgeConditionField(newField);
                    setTimeout(() => handleEdgeConditionChange({ field: newField }), 0);
                  }}
                  disabled={readOnly}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                >
                  <option value="">Selecione um campo</option>
                  <optgroup label="Respostas do Cliente">
                    <option value="last_response">Última Resposta</option>
                    <option value="last_response_type">Tipo da Resposta (button/text)</option>
                  </optgroup>
                  <optgroup label="Dados do Cliente">
                    <option value="contact.name">Nome do Cliente</option>
                    <option value="contact.phone">Telefone do Cliente</option>
                  </optgroup>
                  <optgroup label="Dados do Sistema">
                    <option value="protocol_id">Número do Protocolo</option>
                    <option value="conversation_id">ID da Conversa</option>
                    <option value="current_date">Data Atual</option>
                    <option value="current_time">Hora Atual</option>
                  </optgroup>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Escolha qual informação será comparada
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Operador
                </label>
                <select
                  value={edgeConditionOperator}
                  onChange={(e) => {
                    const newOperator = e.target.value as EdgeCondition['operator'];
                    setEdgeConditionOperator(newOperator);
                    setTimeout(() => handleEdgeConditionChange({ operator: newOperator }), 0);
                  }}
                  disabled={readOnly}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                >
                  <option value="equals">Igual a</option>
                  <option value="not_equals">Diferente de</option>
                  <option value="contains">Contém</option>
                  <option value="not_contains">Não contém</option>
                  <option value="starts_with">Começa com</option>
                  <option value="ends_with">Termina com</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                  Valor
                </label>
                <input
                  type="text"
                  value={edgeConditionValue}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    console.log('[PropertiesPanel] Edge value input changed:', newValue);
                    console.log('[PropertiesPanel] Length:', newValue.length);
                    console.log('[PropertiesPanel] Chars:', newValue.split('').map((c: string) => `${c}(${c.charCodeAt(0)})`).join(', '));
                    setEdgeConditionValue(newValue);
                    setTimeout(() => handleEdgeConditionChange({ value: newValue }), 0);
                  }}
                  disabled={readOnly}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed text-sm"
                  placeholder="sim"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Node Properties
  if (!selectedNode) return null;

  return (
    <div className="w-64 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-y-auto">
      <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Propriedades {readOnly && <span className="text-xs font-normal text-yellow-600 dark:text-yellow-400">(Leitura)</span>}
        </h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-6">
        {!readOnly && onDeleteNode && (
          <button
            onClick={() => {
              onDeleteNode(selectedNode.id);
              onClose();
            }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors text-sm"
          >
            <Trash2 className="w-4 h-4" />
            Deletar Nó
          </button>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tipo do Nó
          </label>
          <div className="px-3 py-2 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300">
            {selectedNode.data.nodeType}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Rótulo (opcional)
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => handleLabelChange(e.target.value)}
            disabled={readOnly}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
            placeholder="Deixe vazio para usar o padrão"
          />
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Configuração</h4>
          {renderConfigForm()}
        </div>
      </div>
    </div>
  );
};
